import { useTranslator } from '@/composables/useTranslator';
import { confirmAction } from '@/composables/useConfirmAction';
import { pushLegacyToast } from './legacyDialogs';
import { log } from '@/utils/core/vueLogger';
import { checkConnectivity, connectivity, enterOffline, getConnectivityMode, leaveOffline, onConnectivityChange } from '@/sync/connectivity';
import { CAUSE, MODE, offlineAllowedFrom, partialOfflineNotice, requestAction, shouldAskStayOffline, toggleAction, transitionNotice } from '@/utils/domain/connectivityRules';

// Legacy side of the connectivity driven by src/sync/connectivity.js (md/CONNECTIVITY.md):
//   - window.__vueConnectivity { mode, check, switchOffline }: read by public/m.eagle/js/connectivity.js
//     (Offline.state / _state / check) and by Main.js switchOffline(). Installed once on this window, so a
//     reloaded legacy document reads the current mode from its very first script, like __vueI18nStore;
//   - evtConnectivityChanged { state, previous, cause }, published into the attached legacy document, where
//     sessionHelper.js keeps the legacy-only reactions (setCnxUp/Down, evtOffline, re-logon, partial sync);
//   - what sessionHelper.js and switchOffline() showed or stored: the online / offline messages, the refusal
//     when offline is not allowed, the partial offline notice, the OFFLINE_STATE preference and its
//     "stay offline?" question. The offline permission is the legacy one (externis.offlineAllowed /
//     partialOfflineAllowed, set by the locations synchronization).

const t = useTranslator();

const OFFLINE_STATE_KEY = 'OFFLINE_STATE';
const USER_WAIT_MS = 500;
const USER_WAIT_ATTEMPTS = 40;
const TRANSLATIONS_WAIT_MS = 200;
const TRANSLATIONS_WAIT_ATTEMPTS = 25;

let installed = false;
let win = null;
let boundAmplify = null;
let amplifyHandlers = [];
let userTimer = null;
let askedFor = null;

const safe = (fn, fallback = null) => {
	try {
		const value = fn();
		return value === undefined ? fallback : value;
	} catch {
		return fallback;
	}
};

const legacyExternis = () => safe(() => win.externis);
const legacyStorage = () => safe(() => (win.require.defined('storage') ? win.require('storage') : null));
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Kept from the last attached document while the iframe reloads.
function refreshPolicy() {
	const ext = legacyExternis();
	if (ext) connectivity.offlineAllowed = offlineAllowedFrom(ext);
}

// Legacy storage keys are scoped to ctx.user (customer.id.KEY): only usable once the user is known.
const saveOfflineState = (state) => safe(() => legacyStorage().save(OFFLINE_STATE_KEY, { state, createdDate: Date.now() }));
const deleteOfflineState = () => safe(() => legacyStorage().deleteItem(OFFLINE_STATE_KEY));

function readOfflineState() {
	return new Promise((resolve) => {
		const storage = legacyStorage();
		if (!storage) return resolve(null);
		try {
			storage.get(OFFLINE_STATE_KEY, resolve);
		} catch {
			resolve(null);
		}
	});
}

function enter(mode, cause) {
	saveOfflineState(mode);
	enterOffline(mode, cause);
}

function leave() {
	saveOfflineState(MODE.UP);
	return leaveOffline();
}

// The chrome connection button, and Main.js switchOffline() without argument.
export function toggleOffline() {
	refreshPolicy();
	switch (toggleAction(getConnectivityMode(), connectivity.offlineAllowed)) {
		case 'refuse':
			confirmAction({ title: t('OfflineMode'), lines: [t('OfflineMode_NotAllowed')], icon: 'wifi_off', confirmText: 'OK', cancelText: null, run: () => {} });
			return;
		case 'enter':
			enter(MODE.MANUAL_DOWN, CAUSE.MANUAL);
			if (partialOfflineNotice(legacyExternis())) pushLegacyToast(t('OnlyYourDeliveryLocationInfosWereSync'), 'info');
			return;
		case 'check':
			checkConnectivity();
			return;
		default:
			leave();
	}
}

// Main.js switchOffline(state) from the legacy itself. Its swal refusal is not reproduced: the callers
// (sync and XHR errors) raise their own XHRError swal right after, which used to replace it at once.
export function requestOfflineMode(state) {
	refreshPolicy();
	switch (requestAction(getConnectivityMode(), state, connectivity.offlineAllowed)) {
		case 'refuse':
			log('warn', 'Connectivity', `${state} requested by the legacy while offline is not allowed`);
			return;
		case 'enter':
			enter(state, state === MODE.AUTO_DOWN ? CAUSE.API : CAUSE.MANUAL);
			return;
		case 'check':
			checkConnectivity();
			return;
		case 'leave':
			leave();
			return;
		default:
	}
}

function onChange(change) {
	refreshPolicy();
	const notice = transitionNotice(change, connectivity.offlineAllowed);
	if (notice === 'up') pushLegacyToast(`${t('Online_Title')} - ${t('Online_Detail')}`, 'success');
	if (notice === 'down') pushLegacyToast(`${t('Offline_Title')} - ${t('Offline_Detail')}`, 'info');
	if (boundAmplify) safe(() => boundAmplify.publish('evtConnectivityChanged', { ...change }));
}

// main.js, before the iframe exists.
export function installConnectivityHost(target = window) {
	if (installed) return;
	installed = true;
	target.__vueConnectivity = {
		mode: getConnectivityMode,
		check: () => void checkConnectivity(),
		switchOffline: (state) => (state ? requestOfflineMode(state) : toggleOffline()),
	};
	onConnectivityChange(onChange);
}

async function waitForTranslations(current) {
	for (let i = 0; i < TRANSLATIONS_WAIT_ATTEMPTS && !safe(() => current.translationsReady(), true); i++) await wait(TRANSLATIONS_WAIT_MS);
}

// sessionHelper.js checkForOffline(): once per legacy document and user (a login switches user).
async function askStayOffline() {
	const current = win;
	const user = safe(() => current.externis.ctx.user);
	const doc = safe(() => current.document);
	if (!user?.id || (askedFor?.doc === doc && askedFor?.userId === user.id)) return;
	askedFor = { doc, userId: user.id };

	await waitForTranslations(current);
	if (win !== current) return;
	const stored = await readOfflineState();
	if (win !== current || !shouldAskStayOffline(stored, getConnectivityMode())) return;

	confirmAction({
		title: t('StayOfflineTitle'),
		lines: [t('StayOfflineQuestion')],
		icon: 'wifi_off',
		confirmText: t('Yes'),
		cancelText: t('No'),
		run: () => requestOfflineMode(MODE.MANUAL_DOWN),
		cancel: deleteOfflineState,
	});
}

// The user is restored a little after externis exists: retry until it is known.
function askWhenUserKnown(attempt = 0) {
	clearTimeout(userTimer);
	userTimer = null;
	if (!win) return;
	if (safe(() => win.externis.ctx.user.id, 0)) {
		askStayOffline();
		return;
	}
	if (attempt < USER_WAIT_ATTEMPTS) userTimer = setTimeout(askWhenUserKnown, USER_WAIT_MS, attempt + 1);
}

// Called by iframeWatcher once amplify and externis exist in a freshly loaded legacy document.
export function attachLegacyConnectivity(iframeWindow) {
	if (!iframeWindow || win === iframeWindow) return;
	detachLegacyConnectivity();
	win = iframeWindow;

	const amplify = safe(() => win.amplify);
	if (amplify?.subscribe) {
		boundAmplify = amplify;
		amplifyHandlers = [
			['evtUserChanged', () => askWhenUserKnown()],
			['evtSynchroEnd', refreshPolicy],
		];
		amplifyHandlers.forEach(([topic, fn]) => safe(() => amplify.subscribe(topic, fn)));
	}

	refreshPolicy();
	askWhenUserKnown();
}

// Called on every iframe load, before anything reads the new document.
export function detachLegacyConnectivity() {
	clearTimeout(userTimer);
	userTimer = null;
	if (boundAmplify) amplifyHandlers.forEach(([topic, fn]) => safe(() => boundAmplify.unsubscribe(topic, fn)));
	boundAmplify = null;
	amplifyHandlers = [];
	win = null;
}
